import React, {useCallback} from 'react';
import {Modal} from 'react-bootstrap';
import {CBCButton} from 'shared/helpers';
import styled from 'styled-components';
import {Flex} from 'shared/components/StyledComponents';
import {size} from 'shared/Breakpoints';

const StyledFooter = styled(Modal.Footer)`
    background: rgb(var(--primary_colour));
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    padding: 12px 20px;
    justify-content: space-between;
    gap: 5px;
    @media (max-width: ${size.md}px) {
        justify-content: center;
    }
`;

const ButtonContainer = styled(Flex)`
    @media (max-width: ${size.md}px) {
        flex-direction: column;
        width: 100%;
        gap: 12px;
    }
    & > .button-transparent {
        padding: 5px 10px !important;
        min-height: 40px;
        border: 2px solid white !important;
        border-radius: 40px !important;
        &:hover {
            border: 2px solid rgb(var(--secondary_colour)) !important;
            background: rgb(var(--secondary_colour));
        }
    }
`;

// FIXME: Use CBCButton instead of inline component when we have
// a proper cypress test.
const Button = styled((props) => <CBCButton {...props} />)`
    border-radius: 40px !important;
    padding: 5px 10px 5px 5px !important;
    width: fit-content !important;
    font-size: 0.95em !important;
    &:hover {
        border: 1px solid white;
    }
    @media (max-width: ${size.md}px) {
        font-size: 0.8em !important;
        width: 80% !important;
    }
`;

export enum CloseEventType {
    CANCEL = 'cancel',
    APPLY = 'apply',
    OK = 'ok',
}

interface FooterInterface {
    yesButtonText?: string;
    noButtonText?: string;
    okButtonText?: string;
    onClose?: (event: CloseEventType) => void | Promise<void>;
    className?: string;
    noIcon?: string;
    okIcon?: string;
    okButtonClassname?: string;
    okButtonDisabled?: boolean;
    reverseOrder?: boolean;
}

const Footer = ({
    yesButtonText,
    noButtonText,
    okButtonText,
    onClose,
    className,
    noIcon = 'Button-Cancel.svg',
    okIcon,
    okButtonClassname,
    okButtonDisabled = false,
    reverseOrder = false,
}: FooterInterface) => {
    const cancel = useCallback(() => {
        if (onClose) {
            void onClose(CloseEventType.CANCEL);
        }
    }, [onClose]);

    const apply = useCallback(() => {
        if (onClose) {
            void onClose(CloseEventType.APPLY);
        }
    }, [onClose]);

    const ok = useCallback(() => {
        if (onClose) {
            void onClose(CloseEventType.OK);
        }
    }, [onClose]);

    const noButton = noButtonText ? (
        <Button
            key="no"
            onClick={cancel}
            iconName={noIcon}
            className="job-button button-light"
            style={{color: '#8A8080'}}>
            {noButtonText}
        </Button>
    ) : null;

    const confirmButtons = (
        <ButtonContainer key="confirm" $gap="20px" $alignItems="center">
            <>
                {!!okButtonText ? (
                    <Button
                        disabled={okButtonDisabled}
                        onClick={ok}
                        type="button"
                        iconName={okIcon}
                        className={
                            okButtonClassname
                                ? okButtonClassname
                                : 'job-button button-blue'
                        }>
                        {okButtonText}
                    </Button>
                ) : null}

                {!!yesButtonText ? (
                    <Button
                        onClick={apply}
                        type="button"
                        iconName="Button-Tick.svg"
                        className="job-button button-blue">
                        {yesButtonText}
                    </Button>
                ) : null}
            </>
        </ButtonContainer>
    );

    return (
        <StyledFooter className={className}>
            {reverseOrder
                ? [confirmButtons, noButton]
                : [noButton, confirmButtons]}
        </StyledFooter>
    );
};

export default Footer;
